import { type JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Link, Navigate, useParams } from 'react-router-dom';
import TextFieldFormGroup, { type ITextFieldFormGroupState } from '../../../Shared/TextFieldFormGroup';
import ValidationInfo from '../../../../validation/validation-info';
import type IValidatable from '../../../Shared/validatable.interface';
import type { IServerValidationError } from '../../../Shared/validatable.interface';
import { ValidatableComponent, ValidationHelper } from '../../../../helpers/validation.helper';
import { AnalyticsService } from '../../../../services/analytics-service';
import useFetch, { Status } from '../../../../hooks/fetch.hook';
import ValidationSummary from '../../../Shared/ValidationSummary';
import Spinner from '../../../Shared/Spinner';
import type IParticipation from '../../../../models/pa-egna-ben/participation.model';
import ParticipationApiModel from '../../models/participation.api-model';
import type LegReportApiModel from '../../models/leg-report.api-model';
import { LegReportStatus, ParticipationHelper } from '../../helpers/participation.helper';
import { SystemClock } from '../../services/system-clock.service';
import Exercises from './components/Exercises';
import Breadcrumb from '../Breadcrumb';
import penIcon from '../../../../../assets/icons/pen-default-blue-1000.svg';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import PageIntroduction from '../../Shared/PageIntroduction';
import InformationBox from '../../Shared/InformationBox';
import { Role } from '../../../../models/pa-egna-ben/role.enum';
import SelectFormGroup from '../../../Shared/SelectFormGroup';
import NotFoundRedirect from '../NotFoundRedirect';
import { InformationBoxType } from '../InformationBox/information-box-type.enum';
import AntiforgeryService from '../../../../services/anti-forgery.service';

function processRecievedData(data: ParticipationApiModel | undefined): IParticipation | undefined {
  return data ? ParticipationApiModel.mapToBusinessModel(data) : undefined;
}

function InvalidScoreValidationSummaryError() {
  return (
    <InformationBox
      type={InformationBoxType.Error}
      headingElement={<span className="h3">Felaktigt antal poäng inrapporterade</span>}
      textElement={
        <>
          <p>Kontrollera att du angett rätt poäng och att antalet elever stämmer.</p>
          <ul className="ms-3 mb-0">
            <li>Det är endast möjligt att samla poäng på skoldagar.</li>
            <li>En elev kan få högst 3 poäng per dag (1 poäng per resväg och 1 bonuspoäng om en anhörig deltagit).</li>
            <li>Har du till exempel 20 elever i klassen kan ni som mest samla ihop 300 poäng per vecka (20 elever x 3 poäng x 5 dagar = 300).</li>
          </ul>
        </>
      }
      className="mb-3"
    />
  );
}

function LegReportConflictErrorMessageBox(): JSX.Element {
  return (
    <InformationBox
      type={InformationBoxType.Error}
      headingElement={<span className="h3">Det gick inte att rapportera poäng för etappen</span>}
      textElement={
        <>
          <p className="mb-0">Poäng har redan rapporterats in.</p>
        </>
      }
      className="mb-3"
    />
  );
}

function ReportScoreNotAllowedErrorMessageBox(): JSX.Element {
  return (
    <InformationBox
      type={InformationBoxType.Error}
      headingElement={<span className="h3">Det gick inte att rapportera poäng för etappen</span>}
      textElement={
        <>
          <p className="mb-0">Rapporteringen är stängd.</p>
        </>
      }
      className="mb-3"
    />
  );
}

function RegisterScore({ viewerRole = Role.teacher }: { viewerRole?: Role }) {
  // === PARAMS ===
  const { participationId, legNumber } = useParams();

  // === STATE ===
  const [getParticipation] = useFetch<ParticipationApiModel>(`/api/paegnaben/${viewerRole}/participations/${participationId}`);
  const [numberOfPointsState, setNumberOfPointsState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [numberOfStudyDaysState, setNumberOfStudyDaysState] = useState<ITextFieldFormGroupState>({ value: '0', validationInfo: new ValidationInfo() });
  const [numberOfSickDaysState, setNumberOfSickDaysState] = useState<ITextFieldFormGroupState>({ value: '', validationInfo: new ValidationInfo() });
  const [selectedExercises, setSelectedExercises] = useState<string[]>([]);
  const [submitStatus, setSubmitStatus] = useState<Status>(Status.notperformed);
  const [submitErrorCode, setSubmitErrorCode] = useState<number | undefined>(undefined);
  const [legHasLegReportError, setLegHasLegReportError] = useState<boolean>(false);
  const [leportScoreNotAllowedError, setReportScoreNotAllowedError] = useState<boolean>(false);

  // === REFS ===
  const numberOfPointsRef = useRef<IValidatable>(null!);
  const numberOfStudyDaysRef = useRef<IValidatable>(null!);
  const numberOfSickDaysRef = useRef<IValidatable>(null!);

  // === HELPER FUNCTIONS ===
  function getValidatableComponents(): ValidatableComponent[] {
    return [
      { component: numberOfPointsRef.current, validationInfo: numberOfPointsState.validationInfo, setState: setNumberOfPointsState },
      { component: numberOfStudyDaysRef.current, validationInfo: numberOfStudyDaysState.validationInfo, setState: setNumberOfStudyDaysState },
      { component: numberOfSickDaysRef.current, validationInfo: numberOfSickDaysState.validationInfo, setState: setNumberOfSickDaysState },
    ];
  }

  function handleServerValidationErrors(errors: IServerValidationError[]): void {
    if (!errors) {
      setReportScoreNotAllowedError(true);
      return;
    }
    const invalidValidationInfos = ValidationHelper.handleServerValidationErrors(errors, getValidatableComponents());
    ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
      AnalyticsService.addEvent(viewerRole === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
    });
  }

  // === VARIABLES ===
  const validationSummaryErrors: string[] = ValidationHelper.getValidationSummaryErrors([
    // NV-6475: Do not include numberOfPointsState invalidation summary, use special error message instead (InvalidScoreValidationSummaryError)
    { validationInfo: numberOfStudyDaysState.validationInfo },
    { validationInfo: numberOfSickDaysState.validationInfo },
  ]);
  const participation = processRecievedData(getParticipation.data);
  const leg = participation?.competition?.legs.find((x) => legNumber && x.number === parseInt(legNumber, 10));
  const legReportStatus = participation && leg && ParticipationHelper.getLegReportStatus(participation, leg, SystemClock.now());

  // === EVENT HANDLERS ===
  async function handleButtonClick(ev: React.MouseEvent<HTMLButtonElement>) {
    ev.preventDefault();

    let isFormValid: boolean = false;
    let invalidValidationInfos: ValidationInfo[];
    let submitResult: { status: Status; errorCode: number | undefined } | undefined;
    await flushSync(async () => {
      if (submitStatus === Status.loading) return;

      setSubmitStatus(Status.notperformed);
      setSubmitErrorCode(undefined);

      [isFormValid, invalidValidationInfos] = ValidationHelper.checkValidity(getValidatableComponents());
      if (!isFormValid) {
        ValidationHelper.trackValidationErrors(invalidValidationInfos, (e) => {
          AnalyticsService.addEvent(viewerRole === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, AnalyticsService.ErrorAction, e);
        });
        return;
      }

      if (!participationId || !leg) {
        throw new Error('participationId or leg is missing');
      }

      setSubmitStatus(Status.loading);
      const apiModel: LegReportApiModel = {
        id: -1,
        participationId: parseInt(participationId, 10),
        legId: leg.id,
        leg: undefined,
        numberOfStudents: -1, // The number of students is set server-side
        score: parseInt(numberOfPointsState.value, 10),
        sickDays: parseInt(numberOfSickDaysState.value, 10),
        studyDays: parseInt(numberOfStudyDaysState.value, 10),
        completedExercises: selectedExercises,
      };

      try {
        const response = await fetch(`/api/paegnaben/${viewerRole}/participations/${participationId}/report-score`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest', 'X-XSRF-TOKEN': await AntiforgeryService.getToken() },
          body: JSON.stringify(apiModel),
        });
        if (response.ok) {
          setSubmitStatus(Status.success);
          submitResult = { status: Status.success, errorCode: undefined };
          AnalyticsService.addEvent(viewerRole === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, 'Etapprapport skickad', '');
        } else {
          setSubmitStatus(Status.failed);
          setSubmitErrorCode(response.status);
          submitResult = { status: Status.failed, errorCode: response.status };
          if (response.status === 400) {
            handleServerValidationErrors((await response.json()).errors);
          }
          if (response.status === 409) {
            setLegHasLegReportError(true);
            document.getElementById('form')?.scrollIntoView();
            document.getElementById('form-validation-summary')?.focus();
          }
          AnalyticsService.addEvent(viewerRole === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, 'Etapprapport skickad - misslyckat', '');
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setSubmitStatus(Status.failed);
        submitResult = { status: Status.failed, errorCode: error };
      }
    });
    if (!isFormValid || submitResult?.errorCode === 400) {
      document.getElementById('form')?.scrollIntoView();
      document.getElementById('form-validation-summary')?.focus();
    }
  }

  // === EFFECTS ===
  useEffect(() => {
    if (getParticipation.data && legNumber) {
      const currentLegReport = getParticipation.data.legReports?.find((lr) => lr.leg && lr.leg.number === parseInt(legNumber, 10));
      if (currentLegReport) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setNumberOfPointsState({ value: currentLegReport.score.toString(), validationInfo: new ValidationInfo() });
        setNumberOfStudyDaysState({ value: currentLegReport.studyDays.toString(), validationInfo: new ValidationInfo() });
        setNumberOfSickDaysState({ value: currentLegReport.sickDays.toString(), validationInfo: new ValidationInfo() });
        setSelectedExercises(currentLegReport.completedExercises);
      }
    }
  }, [getParticipation.data, legNumber]);

  // === RENDER ===
  if (submitStatus === Status.success) {
    const queryParams = new URLSearchParams({ a: 'poang-registrerade' }).toString();
    return <Navigate to={`/klasser/${participationId}?${queryParams}`} />;
  }

  if (getParticipation.httpResponseStatusCode === 401 || submitErrorCode === 401) {
    return <Navigate to="/session-utgangen" />;
  }
  if (getParticipation.httpResponseStatusCode === 403 || submitErrorCode === 403) {
    return <Navigate to="/atkomst-saknas" />;
  }
  if (getParticipation.httpResponseStatusCode === 404 || submitErrorCode === 404) {
    return <NotFoundRedirect />;
  }

  return (
    <div>
      {getParticipation.status === Status.loading && <Spinner />}
      {getParticipation.status === Status.success && participation && (
        <>
          <Breadcrumb
            items={[
              { text: 'Mina sidor', url: '/' },
              ...(viewerRole === Role.municipalityAdministrator ? [{ text: participation?.school?.municipality?.name ?? '', url: `/kommuner/${participation?.school?.municipality?.gid}` }] : []),
              { text: ParticipationHelper.getFullName(participation), url: `/klasser/${participationId}` },
              { text: 'Rapportera poäng', url: undefined },
            ]}
          />
          <PageIntroduction
            heading="Rapportera poäng"
            text={viewerRole === Role.teacher ? 'Du rapporterar veckans samlade poäng vid ett tillfälle. Observera att det inte är möjligt att redigera inrapporterade poäng i efterhand.' : ''}
            requiredText={true}
          />
          {leg && legReportStatus !== LegReportStatus.open && viewerRole !== Role.municipalityAdministrator && (
            <div>
              Det går inte att rapportera poäng för denna etapp just nu.&nbsp;
              {legReportStatus === LegReportStatus.earilerOpenLegExists && 'Det finns en tidigare etapp som är öppen.'}
              {legReportStatus === LegReportStatus.missed && 'Rapporteringen är stängd.'}
              {legReportStatus === LegReportStatus.notOpen && 'Rapporteringen har inte öppnat än.'}
            </div>
          )}
          {leg && (legReportStatus === LegReportStatus.open || Role.municipalityAdministrator) && (
            <>
              <InformationBox
                type={InformationBoxType.Default}
                headingElement={
                  <h2 className="h4">
                    {participation.name}, {participation.school?.name}, {participation.numberOfStudents} elever
                  </h2>
                }
                textElement={
                  <>
                    <p>Om klassens elevantal inte stämmer så uppdaterar ni det innan ni rapporterar veckans poäng.</p>
                    <Link
                      to={`/klasser/${participation.id}/klassuppgifter`}
                      className="d-flex align-items-center"
                      onClick={() =>
                        AnalyticsService.addEvent(viewerRole === Role.teacher ? AnalyticsService.PaEgnaBenTeacher : AnalyticsService.PaEgnaBenAdmin, 'Rapportera poäng, klick på `Klassuppgifter`')
                      }
                    >
                      <img src={DataUrlHelper.encode(penIcon)} alt="" className="me-1" /> Klassuppgifter
                    </Link>
                  </>
                }
                className="mb-4 mb-md-5"
              />
              <form id="form">
                <div id="form-validation-summary" tabIndex={-1}>
                  <ValidationSummary validationSummaryErrors={validationSummaryErrors} />
                  {numberOfPointsState.validationInfo.isInvalid && <InvalidScoreValidationSummaryError />}
                  {legHasLegReportError && <LegReportConflictErrorMessageBox />}
                  {leportScoreNotAllowedError && <ReportScoreNotAllowedErrorMessageBox />}
                </div>
                <h3 className="mb-3">Etapp {legNumber} av 5</h3>
                <TextFieldFormGroup
                  ref={numberOfPointsRef}
                  labelText="Veckans poäng"
                  helpTextElement={<span>Ange klassens poäng och eventuella bonuspoäng</span>}
                  required={true}
                  inputId="number-of-points-input"
                  serverValidationName="score"
                  type="number"
                  state={[numberOfPointsState, setNumberOfPointsState]}
                />
                <SelectFormGroup
                  id="number-of-study-days-select"
                  labelText="Antal studiedagar"
                  required={true}
                  ref={numberOfStudyDaysRef}
                  options={[
                    { value: '0', text: '0' },
                    { value: '1', text: '1' },
                    { value: '2', text: '2' },
                    { value: '3', text: '3' },
                    { value: '4', text: '4' },
                    { value: '5', text: '5' },
                  ]}
                  serverValidationName="studyDays"
                  state={numberOfStudyDaysState}
                  onChange={(value) => setNumberOfStudyDaysState({ value, validationInfo: new ValidationInfo() })}
                />
                <TextFieldFormGroup
                  ref={numberOfSickDaysRef}
                  labelText="Antal sjukdagar"
                  helpTextElement={<span>Om till exempel två elever är sjuka under två dagar anger du fyra sjukdagar</span>}
                  required={true}
                  inputId="number-of-sick-days-input"
                  type="number"
                  serverValidationName="sickDays"
                  state={[numberOfSickDaysState, setNumberOfSickDaysState]}
                />
                <Exercises leg={leg} selectedExercises={selectedExercises} legReports={participation.legReports} className="mb-5" onChange={setSelectedExercises} />
                <div className="d-grid d-md-flex gap-3">
                  <Link to={`/klasser/${participationId}`} className="btn btn-secondary flex-grow-1 flex-basis-0 order-2 order-md-1">
                    Avbryt
                  </Link>
                  <button type="button" className="btn btn-primary flex-grow-1 flex-basis-0  order-1 order-md-2" onClick={handleButtonClick}>
                    Rapportera poäng
                  </button>
                </div>
              </form>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default RegisterScore;
